import React, { useEffect, useState } from 'react';
import { transcriptService, TranscriptToken } from '../../services/transcriptService';
import { useToast } from '../../components/Toast';

const AdminTranscriptTokensPage: React.FC = () => {
  const { showToast } = useToast();
  const [tokens, setTokens] = useState<TranscriptToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    token: '',
    is_active: true,
  });

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const data = await transcriptService.getTokens();
      setTokens(data);
    } catch (error: any) {
      showToast('Lỗi khi tải danh sách tokens', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.token.trim()) {
      showToast('Vui lòng nhập token', 'warning');
      return;
    }

    try {
      await transcriptService.createToken(formData.token, formData.is_active);
      showToast('Tạo token thành công', 'success');
      setShowCreateForm(false);
      resetForm();
      fetchTokens();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi tạo token', 'error');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    if (!formData.token.trim()) {
      showToast('Vui lòng nhập token', 'warning');
      return;
    }

    try {
      await transcriptService.updateToken(editingId, {
        token: formData.token,
        is_active: formData.is_active,
      });
      showToast('Cập nhật thành công', 'success');
      setEditingId(null);
      resetForm();
      fetchTokens();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc muốn xóa token này?')) return;
    try {
      await transcriptService.deleteToken(id);
      showToast('Xóa thành công', 'success');
      fetchTokens();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa', 'error');
    }
  };

  const handleEdit = async (token: TranscriptToken) => {
    setEditingId(token.id);
    // Nếu token đã bị rút gọn (có '...'), lấy token đầy đủ từ API
    if (token.token.includes('...')) {
      try {
        const fullToken = await transcriptService.getToken(token.id);
        setFormData({
          token: fullToken.token,
          is_active: fullToken.is_active,
        });
      } catch (error: any) {
        showToast('Không thể lấy token đầy đủ. Vui lòng nhập lại token.', 'warning');
        setFormData({
          token: '',
          is_active: token.is_active,
        });
      }
    } else {
      setFormData({
        token: token.token,
        is_active: token.is_active,
      });
    }
    setShowCreateForm(true);
  };

  const handleToggleActive = async (token: TranscriptToken) => {
    try {
      await transcriptService.updateToken(token.id, {
        is_active: !token.is_active,
      });
      showToast('Cập nhật trạng thái thành công', 'success');
      fetchTokens();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      token: '',
      is_active: true,
    });
    setEditingId(null);
  };

  const activeCount = tokens.filter((t) => t.is_active).length;
  const totalUsage = tokens.reduce((sum, t) => sum + t.usage_count, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Quản lý Transcript Tokens</h1>
        <p className="text-gray-600">
          Quản lý các authorization tokens để lấy transcript từ YouTube
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Tổng số tokens</div>
          <div className="text-2xl font-bold text-gray-900">{tokens.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Tokens đang hoạt động</div>
          <div className="text-2xl font-bold text-green-600">{activeCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Tổng số lần sử dụng</div>
          <div className="text-2xl font-bold text-blue-600">{totalUsage}</div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">
            {editingId ? 'Chỉnh sửa Token' : 'Thêm Token Mới'}
          </h2>
          <form onSubmit={editingId ? handleUpdate : handleCreate}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authorization Token (Bearer)
                </label>
                <textarea
                  value={formData.token}
                  onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                  placeholder="Dán token vào đây (có thể có hoặc không có prefix 'Bearer ')"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Token sẽ được xoay vòng tự động khi sử dụng
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Kích hoạt token
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  {editingId ? 'Cập nhật' : 'Tạo mới'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Hủy
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Actions */}
      {!showCreateForm && (
        <div className="mb-6">
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            + Thêm Token Mới
          </button>
        </div>
      )}

      {/* Tokens List */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Đang tải...</p>
          </div>
        ) : tokens.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Chưa có token nào. Hãy thêm token đầu tiên.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token (rút gọn)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Số lần sử dụng
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lần sử dụng cuối
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ngày tạo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tokens.map((token) => (
                  <tr key={token.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {token.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                      {token.token}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          token.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {token.is_active ? 'Hoạt động' : 'Tắt'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {token.usage_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {token.last_used_at
                        ? new Date(token.last_used_at).toLocaleString('vi-VN')
                        : 'Chưa sử dụng'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {token.created_at
                        ? new Date(token.created_at).toLocaleString('vi-VN')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleToggleActive(token)}
                          className={`px-3 py-1 rounded text-xs ${
                            token.is_active
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {token.is_active ? 'Tắt' : 'Bật'}
                        </button>
                        <button
                          onClick={() => handleEdit(token)}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                        >
                          Sửa
                        </button>
                        <button
                          onClick={() => handleDelete(token.id)}
                          className="px-3 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200"
                        >
                          Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTranscriptTokensPage;

